import { createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { apiGet, apiPut, apiDelete, apiPost } from '../../../psUtil';
import type { 
    DealerTier, 
    PowerShadesAPIResponse, 
    loadStatusType 
} from '../../../powershadesApiTypes';
import { extraEntityReducersFunction, loadStatus } from '../entitiesType';
import { UsePortalDispatch } from '../../index';
import type { AppState } from '../../index';

export interface DealerTiersState {
    tiers: DealerTier[];
    loadStatus: loadStatusType;
    error: string | null;
}

export const initialDealerTiersState: () => DealerTiersState = () => ({
    tiers: [],
    loadStatus: loadStatus.notLoaded,
    error: null,
    selectedTier: null,
});

// Fetch all dealer tiers
export const fetchDealerTiers = createAsyncThunk<
    DealerTier[],
    void,
    {
        state: AppState;
        rejectValue: string;
    }
>('dealerTiers/fetchDealerTiers', async (_, { rejectWithValue }) => {
    try {
        const response = await apiGet<DealerTier[]>('DealerTiers');
        return response.data;
    } catch (error) {
        return rejectWithValue('Failed to fetch dealer tiers');
    }
});

// View a specific dealer tier
export const viewDealerTier = createAsyncThunk<
    DealerTier,
    number,
    {
        state: AppState;
        rejectValue: string;
    }
>('dealerTiers/viewDealerTier', async (tierId, { rejectWithValue }) => {
    try {
        const response = await apiGet<PowerShadesAPIResponse<DealerTier>>(`DealerTiers/${tierId}`);
        return response.data.data;
    } catch (error) {
        return rejectWithValue('Failed to fetch dealer tier details');
    }
});

// Update a dealer tier
export const updateDealerTier = createAsyncThunk<
    DealerTier,
    { id: number; data: Partial<DealerTier> },
    {
        state: AppState;
        rejectValue: string;
    }
>('dealerTiers/updateDealerTier', async ({ id, data }, { rejectWithValue }) => {
    try {
        const response = await apiPut<PowerShadesAPIResponse<DealerTier>>(`DealerTiers/${id}`, data);
        return response.data.data;
    } catch (error) {
        return rejectWithValue('Failed to update dealer tier');
    }
});

// Delete a dealer tier
export const deleteDealerTier = createAsyncThunk<
    number,
    number,
    {
        state: AppState;
        rejectValue: string;
    }
>('dealerTiers/deleteDealerTier', async (tierId, { rejectWithValue }) => {
    try {
        await apiDelete(`DealerTiers/${tierId}`);
        return tierId;
    } catch (error) {
        return rejectWithValue('Failed to delete dealer tier');
    }
});

// Create a new dealer tier
export const createDealerTier = createAsyncThunk<
    DealerTier,
    Partial<DealerTier>,
    {
        state: AppState;
        rejectValue: string;
    }
>('dealerTiers/createDealerTier', async (data, { rejectWithValue }) => {
    try {
        const response = await apiPost<DealerTier>('DealerTiers', data);
        return response.data;
    } catch (error) {
        return rejectWithValue('Failed to create dealer tier');
    }
});

export const dealerTiersBuilder: extraEntityReducersFunction = (builder) => {
    builder
        // Fetch all dealer tiers
        .addCase(fetchDealerTiers.pending, (state) => {
            state.dealerTiers.loadStatus = loadStatus.loading;
        })
        .addCase(fetchDealerTiers.fulfilled, (state, action) => {
            state.dealerTiers.tiers = action.payload;
            state.dealerTiers.loadStatus = loadStatus.fullyLoaded;
            state.dealerTiers.error = null;
        })
        .addCase(fetchDealerTiers.rejected, (state, action) => {
            state.dealerTiers.loadStatus = loadStatus.notLoaded;
            state.dealerTiers.error = action.payload ?? 'Failed to fetch dealer tiers';
        })
        // Update dealer tier
        .addCase(updateDealerTier.fulfilled, (state, action) => {
            const index = state.dealerTiers.tiers.findIndex(tier => tier.id === action.meta.arg.id);
            const dealerTier = state.dealerTiers.tiers[index];
            if (index !== -1 && dealerTier) {
                const updatedTier = { ...dealerTier, ...action.meta.arg.data, id: action.meta.arg.id };
                state.dealerTiers.tiers[index] = updatedTier;
            }
        })
        // Delete dealer tier
        .addCase(deleteDealerTier.fulfilled, (state, action) => {
            state.dealerTiers.tiers = state.dealerTiers.tiers.filter(tier => tier.id !== action.payload);
        })
        // Create dealer tier
        .addCase(createDealerTier.fulfilled, (state, action) => {
            state.dealerTiers.tiers.push(action.payload);
        });
};

export const selectDealerTiers = (state: AppState) => state.entity.dealerTiers.tiers;
export const selectDealerTiersLoadStatus = (state: AppState) => state.entity.dealerTiers.loadStatus;
export const selectDealerTiersError = (state: AppState) => state.entity.dealerTiers.error;

export const useDealerTiers = () => {
    const dispatch = UsePortalDispatch();
    const tiers = useSelector(selectDealerTiers);
    const currentLoadStatus = useSelector(selectDealerTiersLoadStatus);
    const error = useSelector(selectDealerTiersError);

    useEffect(() => {
        if (currentLoadStatus === loadStatus.notLoaded) {
            dispatch(fetchDealerTiers());
        }
    }, [dispatch, currentLoadStatus]);

    return {
        tiers:  tiers ?? [],
        currentLoadStatus,
        error,
        viewTier: (id: number) => dispatch(viewDealerTier(id)),
        updateTier: (id: number, data: Partial<DealerTier>) => dispatch(updateDealerTier({ id, data })),
        deleteTier: (id: number) => dispatch(deleteDealerTier(id)),
        createTier: (data: Partial<DealerTier>) => dispatch(createDealerTier(data)),
    };
};
